import { State as DataState } from '@progress/kendo-data-query';
import { produce } from 'immer';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { customEvents } from '../../../../../common/constants';
import Breadcrumbs from '../../../../../common/widgets/Breadcrumbs';
import { getSkip } from '../../../../../common/widgets/Grid';
import { config } from '../../../config';
import { SubscriptionItemModel } from '../../../models/Subscriptions';
import { resources } from '../../../resources/resources';
import applicationCrumbs from '../../ApplicationCrumbs';
import PlatformInformation from '../../Common/PlatformInformation';
import MyAccountMenu from '../Common/MyAccountMenu';
import { SubscriptionGrid } from './Components/SubscriptionsGrid';

interface SubscriptionsPageState {
  subscriptions: {
    data?: SubscriptionItemModel[];
    total: number;
    dataState: DataState;
  };
  showErrorPanel?: boolean;
}

const initialDataState = {
  skip: 0,
  take: config.grid.defaultPageSize,
  sort: [{ field: 'dateAdded', dir: 'desc' }],
};

export const SubscriptionsPage = () => {
  const { t: translate } = useTranslation();
  const crumbs = applicationCrumbs(translate);
  const [searchParams] = useSearchParams();
  const odataString = searchParams.get('dataState');
  const [state, setState] = useState<SubscriptionsPageState>({
    subscriptions: {
      total: 0,
      dataState: odataString
        ? JSON.parse(decodeURI(odataString))
        : initialDataState,
    },
  });

  useEffect(() => {
    setState(
      produce(d => {
        d.subscriptions.dataState = odataString
          ? JSON.parse(decodeURI(odataString))
          : initialDataState;
      }),
    );
  }, [odataString]);

  const onDataStateChange = (dataState: DataState) => {
    setState(
      produce(draft => {
        draft.subscriptions.dataState = {
          ...dataState,
          skip: getSkip(draft.subscriptions.dataState, dataState),
        };
      }),
    );
  };

  const onDataReceived = (data: any, showErrorPanel?: boolean) => {
    setState(
      produce(draft => {
        draft.subscriptions.data = data.data;
        draft.subscriptions.total = data.total;
        draft.showErrorPanel = showErrorPanel;
      }),
    );
  };

  const onSubscriptionCanceled = (orderProductId: number) => {
    setState(
      produce(draft => {
        draft.subscriptions.data = draft.subscriptions.data?.filter(
          e => e.orderProductId !== orderProductId,
        );
      }),
    );
    document.dispatchEvent(
      new CustomEvent(customEvents.refreshCustomerBalances),
    );
  };

  return (
    <>
      <Breadcrumbs
        entries={[crumbs.home, crumbs.accountDashboard, crumbs.subscriptions]}
      />
      <section className="section-main section-actions my-account-page subscriptions-page">
        <div className="container">
          <div className="area-actions">
            <MyAccountMenu />
          </div>
          <div className="area-content">
            <PlatformInformation />
            <div className="panel padding-xl">
              <div className="page-actions">
                <h1>{translate(resources.subscriptions.title)}</h1>
              </div>
              <SubscriptionGrid
                subscriptions={{
                  ...state.subscriptions,
                  onDataReceived,
                  onDataStateChange,
                  onSubscriptionCanceled,
                }}
                showErrorPanel={state.showErrorPanel}
                isForDashboard={false}
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
